import { useEffect, useState } from "react";
import type { IntakeTemplateContent } from "@care-by-ella/contracts";
import { fetchIntakeTemplate } from "@/lib/intake-template-api";

// TES-474 #2 — the weekly check-in is the surface the doctor reads every
// week, and it was raw JSON: `week_wellbeing: 3`. The question text lives in
// the published weekly_checkin template, which the admin can already fetch
// for the editor; here it is used to read, not to edit. Fetched once per
// page load (module cache) — the template does not change between check-ins.
//
// Unknown keys (an answer from an older template version whose field is
// gone) keep their key, so nothing the patient said is hidden. The raw JSON
// stays one click away for the cases the labels cannot explain.

// TES-1561 — per template: an F-05 row reads the published `milestone`.
const labelsPromises = new Map<string, Promise<Record<string, string>>>();
function loadLabels(templateKey: string): Promise<Record<string, string>> {
  let p = labelsPromises.get(templateKey);
  if (!p) {
    p = fetchIntakeTemplate(templateKey)
      .then((bundle) => labelsOf(bundle.published))
      .catch(() => {
        labelsPromises.delete(templateKey); // let the next mount try again
        return {};
      });
    labelsPromises.set(templateKey, p);
  }
  return p;
}

export function labelsOf(
  content: IntakeTemplateContent,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const section of content.sections) {
    for (const field of section.fields) {
      out[field.key] = field.label.sv;
    }
  }
  return out;
}

export function formatAnswer(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Ja" : "Nej";
  if (Array.isArray(value)) return value.map(formatAnswer).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function WeeklyCheckinAnswers({
  answers,
  templateKey = "weekly_checkin",
}: {
  answers: Record<string, unknown>;
  templateKey?: string;
}) {
  const [labels, setLabels] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    let cancelled = false;
    void loadLabels(templateKey).then((l) => {
      if (!cancelled) setLabels(l);
    });
    return () => {
      cancelled = true;
    };
  }, [templateKey]);

  return (
    <div className="mt-xs">
      <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-md gap-y-[2px] text-sm">
        {Object.entries(answers).map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-textSecondary">
              {labels?.[key] ?? <code className="text-xs">{key}</code>}
            </dt>
            <dd className="text-right font-medium text-textPrimary">
              {formatAnswer(value)}
            </dd>
          </div>
        ))}
      </dl>
      <details className="mt-xs">
        <summary className="cursor-pointer text-xs text-textSecondary">
          Rådata
        </summary>
        <pre className="mt-xs text-xs bg-surfaceMuted/40 p-sm rounded-sm overflow-x-auto">
          {JSON.stringify(answers, null, 2)}
        </pre>
      </details>
    </div>
  );
}
